/**
 * Legal document URLs.
 *
 * The Terms, Privacy and Cookies policies are published on the shared Tereiko
 * CDN rather than bundled with the SPA, so Legal can re-issue a version
 * without a frontend deploy. The `/v1/` segment is the policy version — bump
 * it here (and only here) when a new version is published.
 */
const LEGAL_CDN_BASE = 'https://public.assets.tereiko.com/policies';

export const LEGAL_DOCS = {
  termsAndConditions: `${LEGAL_CDN_BASE}/terms-and-conditions/v1/terms-and-conditions.pdf`,
  privacyPolicy: `${LEGAL_CDN_BASE}/privacy/v1/privacy-policy.pdf`,
  cookiesPolicy: `${LEGAL_CDN_BASE}/cookies/v1/cookies-policy.pdf`,
} as const;

/**
 * Editorial version of each published policy — the label the documents (and
 * the persisted signup acceptance proof) carry.
 *
 * Deliberately NOT the `/v1/` segment of the URLs above: that segment is the
 * CDN publishing slot. Mirror of `backend/app/config/legal.py`; bump both
 * together when Legal re-issues a document.
 */
export const LEGAL_VERSIONS = {
  termsAndConditions: 'v3',
  privacyPolicy: 'v2',
  cookiesPolicy: 'v2',
} as const;

/** One document as declared by the client when accepting the terms. */
export interface AcceptedDocumentRef {
  version: string;
  url: string;
}

/** The two documents covered by the required signup checkbox. */
export interface AcceptedDocuments {
  terms: AcceptedDocumentRef;
  privacy: AcceptedDocumentRef;
}

/**
 * Snapshot of the documents the user was shown, sent as `accepted_documents`
 * with a signup payload.
 *
 * The server never trusts this — it records its own registry snapshot and only
 * stores this alongside it so a client/server version drift is detectable.
 */
export function buildAcceptedDocuments(): AcceptedDocuments {
  return {
    terms: {
      version: LEGAL_VERSIONS.termsAndConditions,
      url: LEGAL_DOCS.termsAndConditions,
    },
    privacy: {
      version: LEGAL_VERSIONS.privacyPolicy,
      url: LEGAL_DOCS.privacyPolicy,
    },
  };
}

export default LEGAL_DOCS;
